// @ts-ignore - mammoth.js types
import * as mammoth from 'mammoth';

/**
 * Extract plain text content from various file types
 */
export async function extractTextFromFile(file: File): Promise<string> {
  const fileType = file.type;
  const fileName = file.name.toLowerCase();

  // Text-based formats
  if (fileType === "text/plain" || fileName.endsWith(".txt")) {
    return await extractFromText(file);
  }

  // HTML
  if (fileType === "text/html" || fileName.endsWith(".html")) {
    return await extractFromHTML(file);
  }

  // PDF (basic extraction)
  if (fileType === "application/pdf" || fileName.endsWith(".pdf")) {
    return await extractFromPDF(file);
  }

  // Word Documents (.docx) - NEW: Using mammoth.js for proper extraction
  if (fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || fileName.endsWith(".docx")) {
    return await extractFromDocx(file);
  }

  // Legacy Word (.doc)
  if (fileType === "application/msword" || fileName.endsWith(".doc")) {
    return await extractFromDoc(file);
  }

  // RTF
  if (fileType === "application/rtf" || fileName.endsWith(".rtf")) {
    return await extractFromRTF(file);
  }

  // Excel (basic extraction)
  if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
    return await extractFromExcel(file);
  }

  // PowerPoint (basic extraction)
  if (fileName.endsWith(".pptx") || fileName.endsWith(".ppt")) {
    return await extractFromPowerPoint(file);
  }

  // If we can't determine the type, try as text
  return await extractFromText(file);
}

async function extractFromText(file: File): Promise<string> {
  return await file.text();
}

async function extractFromHTML(file: File): Promise<string> {
  const text = await file.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "text/html");
  
  // Remove script and style tags
  const scripts = doc.querySelectorAll("script, style");
  scripts.forEach(el => el.remove());
  
  // Get text content
  return doc.body.innerText || doc.documentElement.innerText || "";
}

async function extractFromPDF(file: File): Promise<string> {
  // For now, provide a message about PDF extraction
  // In production, you'd use pdf-parse or pdfjs-dist library
  const arrayBuffer = await file.arrayBuffer();
  
  // Simple text extraction - this is basic and won't work for all PDFs
  // For better PDF support, install and use pdf-parse or pdfjs-dist
  const view = new Uint8Array(arrayBuffer);
  let text = "";
  
  // Try to extract ASCII text from PDF
  for (let i = 0; i < view.length; i++) {
    const byte = view[i];
    if ((byte >= 32 && byte <= 126) || byte === 10 || byte === 13) {
      text += String.fromCharCode(byte);
    }
  }
  
  return text || "[PDF extraction requires pdf-parse library for full support]";
}

/**
 * Enhanced DOCX extraction using mammoth.js
 * Properly parses Word documents and extracts formatted text content
 */
async function extractFromDocx(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    
    // Use mammoth to extract document content
    // @ts-ignore - mammoth type issues
    const result = await mammoth.extractRawText({ arrayBuffer });
    
    // Get the text content
    let text = result?.value || result || '';
    
    // Ensure text is a string
    if (typeof text !== 'string') {
      text = String(text);
    }
    
    // Clean up excessive whitespace
    text = text
      .replace(/\n\n+/g, '\n\n') // Remove excessive blank lines
      .replace(/\t+/g, ' ') // Replace tabs with spaces
      .trim();
    
    // If mammoth extraction was successful, return the content
    if (text && text.length > 0) {
      console.log('[DOCX] Successfully extracted', text.length, 'characters using mammoth.js');
      return text;
    }
    
    // Fallback: If no text was extracted, try the alternative method
    console.log('[DOCX] Mammoth returned empty result, trying fallback method');
    return await extractFromDocxFallback(file);
  } catch (error) {
    // If mammoth fails, try the fallback extraction method
    console.log('[DOCX] Mammoth extraction failed:', error, 'trying fallback method');
    return await extractFromDocxFallback(file);
  }
}

/**
 * Fallback DOCX extraction for cases where mammoth fails
 * Extracts text directly from the XML structure of the .docx file
 */
async function extractFromDocxFallback(file: File): Promise<string> {
  try {
    console.log('[DOCX Fallback] Starting fallback extraction for', file.name);
    const arrayBuffer = await file.arrayBuffer();
    const view = new Uint8Array(arrayBuffer);
    
    // Convert to string and extract text between XML tags
    let text = "";
    let validBytes = 0;
    
    for (let i = 0; i < view.length; i++) {
      const byte = view[i];
      if ((byte >= 32 && byte <= 126) || byte === 10 || byte === 13) {
        text += String.fromCharCode(byte);
        validBytes++;
      }
    }
    
    console.log('[DOCX Fallback] Extracted', validBytes, 'valid bytes,', text.length, 'characters');
    
    // Extract text between <w:t> tags (Word text elements)
    const matches = text.match(/<w:t[^>]*>([^<]*)<\/w:t>/g) || [];
    console.log('[DOCX Fallback] Found', matches.length, 'text elements');
    
    const content = matches
      .map(match => match.replace(/<[^>]*>/g, ""))
      .filter(text => text.trim().length > 0)
      .join(" ");
    
    if (content && content.length > 0) {
      console.log('[DOCX Fallback] Successfully extracted', content.length, 'characters');
      return content;
    }
    
    // Last resort: return what we have if anything
    if (text.length > 100) {
      console.log('[DOCX Fallback] No <w:t> tags found, returning raw text');
      return text.substring(0, 5000);
    }
    
    console.log('[DOCX Fallback] Failed - no meaningful content extracted');
    return "[Unable to extract DOCX content - file may be corrupted or in an unsupported format]";
  } catch (error) {
    console.error('[DOCX Fallback] Error:', error);
    return "[Error extracting DOCX content - please ensure the file is a valid Word document]";
  }
}

async function extractFromDoc(_file: File): Promise<string> {
  // Legacy DOC format requires specialized library
  return "[DOC format requires Apache POI or similar library for extraction]";
}

async function extractFromRTF(file: File): Promise<string> {
  const text = await file.text();
  // Remove RTF formatting
  const cleanText = text
    .replace(/\\[a-z]+[\d]*\s?/g, "") // Remove RTF commands
    .replace(/[{}]/g, "") // Remove braces
    .replace(/\\'[0-9a-f]{2}/g, "") // Remove hex codes
    .replace(/\s+/g, " ") // Normalize spaces
    .trim();
  
  return cleanText;
}

async function extractFromExcel(file: File): Promise<string> {
  // For Excel, we'd normally use xlsx library
  // This is a simplified fallback
  const arrayBuffer = await file.arrayBuffer();
  const view = new Uint8Array(arrayBuffer);
  
  let text = "";
  for (let i = 0; i < view.length; i++) {
    const byte = view[i];
    if ((byte >= 32 && byte <= 126) || byte === 10 || byte === 13) {
      text += String.fromCharCode(byte);
    }
  }
  
  return text || "[Excel extraction requires xlsx library for full support]";
}

async function extractFromPowerPoint(file: File): Promise<string> {
  // For PowerPoint, we'd normally use pptx library
  const arrayBuffer = await file.arrayBuffer();
  const view = new Uint8Array(arrayBuffer);
  
  let text = "";
  for (let i = 0; i < view.length; i++) {
    const byte = view[i];
    if ((byte >= 32 && byte <= 126) || byte === 10 || byte === 13) {
      text += String.fromCharCode(byte);
    }
  }
  
  return text || "[PowerPoint extraction requires pptx library for full support]";
}

/**
 * Detect file type from extension or MIME type
 */
export function detectFileType(file: File): string {
  const fileName = file.name.toLowerCase();
  
  if (file.type === "text/plain" || fileName.endsWith(".txt")) return "text";
  if (file.type === "application/pdf" || fileName.endsWith(".pdf")) return "pdf";
  if (fileName.endsWith(".docx")) return "docx";
  if (fileName.endsWith(".doc")) return "doc";
  if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) return "xlsx";
  if (fileName.endsWith(".pptx") || fileName.endsWith(".ppt")) return "pptx";
  if (fileName.endsWith(".html")) return "html";
  if (fileName.endsWith(".rtf")) return "rtf";
  
  return "unknown";
}

/**
 * Get file type enum value
 */
export function getFileTypeEnum(file: File): "text" | "pdf" | "docx" | "doc" | "xlsx" | "pptx" | "html" | "rtf" | "unknown" {
  const type = detectFileType(file);
  const validTypes: Array<"text" | "pdf" | "docx" | "doc" | "xlsx" | "pptx" | "html" | "rtf" | "unknown"> = [
    "text",
    "pdf",
    "docx",
    "doc",
    "xlsx",
    "pptx",
    "html",
    "rtf",
    "unknown",
  ];
  return validTypes.includes(type as any) ? (type as any) : "unknown";
}

/**
 * Get human-readable file type label
 */
export function getFileTypeLabel(fileType: string): string {
  const labels: Record<string, string> = {
    text: "Text File (.txt)",
    pdf: "PDF Document (.pdf)",
    docx: "Word Document (.docx)",
    doc: "Legacy Word Document (.doc)",
    xlsx: "Excel Spreadsheet (.xlsx)",
    pptx: "PowerPoint Presentation (.pptx)",
    html: "HTML Document (.html)",
    rtf: "Rich Text Format (.rtf)",
    unknown: "Unknown Format",
  };
  return labels[fileType] || "Unknown Format";
}
